/**
 * @render react
 * @name Slider
*/

import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { compose } from 'recompose';

import { withPolaris } from 'Components/protons/Polaris';
import { withSticky } from 'Components/protons/withSticky';

import { Icon } from 'Components/atoms/Icon';

import './_style.scss';

/**
 * Slider component
 * @param {Object} settings - tiny slider settings object
 * @param {Node} children - children components to the Slider
 * @param {boolean} loadStyles - Whether to apply the class names for styling
 * @param {boolean} customControls - Whether to use custom controls
 * @param {string} prevIcon - Icon for previous button
 * @param {string} nextIcon - Icon for next button
 * @param {Object} extraClassNames - extra classes to be applied to the component
 * @param {Object} context - extra information coming from Polaris provider
 * @param {number} size - supported tag size
 */

class Slider extends PureComponent {
  /**
   * Constructor
   */
  constructor(props) {
    super(props);

    this.slider = null;
    // create ref
    this.sliderElement = React.createRef();
  }

  /**
   * Once component is mounted, we initialize tns
   * FYI for componentDidMount https://github.com/erikras/react-redux-universal-hot-example/issues/429#issuecomment-151990676
   * in dev mode
   */
  componentDidMount() {
    const { settings, tinySliderEnabled } = this.props;
    const { container } = settings;

    if (!container) {
      settings.container = this.sliderElement;
    }

    if (typeof window !== 'undefined' && tinySliderEnabled === true) {
      // eslint-disable-next-line global-require
      this.slider = require('tiny-slider/src/tiny-slider').tns(settings);
    }
  }

  /**
   * Destroy any carousel that we have
   */
  componentWillUnmount() {
    if (this.slider) {
      this.slider.destroy();
    }
  }

  /**
   * Render our component
   */
  render() {
    const {
      children,
      childrenExtraClassNames,
      context: { cssPrefix },
      customControls,
      extraClassNames,
      id,
      loadStyles,
      nextIcon,
      prevIcon,
    } = this.props;

    return (
      <div
        className={classNames({
          [`${cssPrefix}__slider`]: loadStyles,
          ...extraClassNames,
        })}
      >
        {customControls && (
          <div className={`${cssPrefix}__slider--controls`} aria-label="Slider Controls">
            <button className={`${cssPrefix}__slider--controls--prev`} tabIndex={-1} type="button">
              {prevIcon ? <Icon icon={prevIcon} /> : (<span role="img" aria-label="Previous Slide">◀️</span>)}
            </button>

            <button className={`${cssPrefix}__slider--controls--next`} tabIndex={-1} type="button">
              {nextIcon ? <Icon icon={nextIcon} /> : (<span role="img" aria-label="Next Slide">▶️</span>)}
            </button>
          </div>
        )}

        <div
          ref={this.sliderElement}
          className={classNames({
            [`${cssPrefix}__slider--slides`]: loadStyles,
            ...childrenExtraClassNames,
          })}
          id={id}
        >
          {children}
        </div>
      </div>
    );
  }
}

Slider.propTypes = {
  children: PropTypes.node,
  childrenExtraClassNames: PropTypes.objectOf(PropTypes.bool),
  context: PropTypes.shape(),
  customControls: PropTypes.bool,
  extraClassNames: PropTypes.objectOf(PropTypes.bool),
  id: PropTypes.string,
  loadStyles: PropTypes.bool,
  nextIcon: PropTypes.string,
  prevIcon: PropTypes.string,
  settings: PropTypes.shape(),
  tinySliderEnabled: PropTypes.bool,
};

Slider.defaultProps = {
  children: [],
  childrenExtraClassNames: {},
  context: {},
  customControls: true,
  extraClassNames: {},
  id: null,
  loadStyles: true,
  nextIcon: null,
  prevIcon: null,
  settings: {
    container: '.polaris__slider',
    controlsContainer: '.polaris__slider--controls',
    items: 3,
    slideBy: 'page',
    autoplay: true,
  },
  tinySliderEnabled: true,
};

export default compose(withPolaris, withSticky)(Slider);
